import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react';

import styles from './SizeDial.module.css';

interface Props {
  /** Percentage shown in the middle — board size against the fitted picture. */
  percent: number;
  /** How far through the size range the board is, 0–1. Drives the ring and the knob. */
  fraction: number;
  /** Called with a signed size delta in stage px. */
  onDelta: (delta: number) => void;
  /** Size range in stage px, used to convert angular movement into a size change. */
  range: number;
  disabled?: boolean;
}

/** Degrees of dial rotation that traverse the whole size range. */
const DEGREES_PER_RANGE = 280;

/**
 * The rotary size control.
 *
 * Accumulates the **signed shortest angular delta** between pointer moves rather than
 * snapping the knob to the raw pointer angle — snapping makes the value jump when the
 * pointer crosses the top of the dial or starts off-axis.
 */
export function SizeDial({
  percent,
  fraction,
  onDelta,
  range,
  disabled = false,
}: Props): React.JSX.Element {
  const centre = useRef<{ cx: number; cy: number } | null>(null);
  const lastAngle = useRef<number | null>(null);

  const angleOf = useCallback((event: { clientX: number; clientY: number }): number => {
    const c = centre.current;
    if (c === null) return 0;
    return (Math.atan2(event.clientX - c.cx, -(event.clientY - c.cy)) * 180) / Math.PI;
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      if (disabled) return;
      const rect = event.currentTarget.getBoundingClientRect();
      centre.current = { cx: rect.left + rect.width / 2, cy: rect.top + rect.height / 2 };
      lastAngle.current = angleOf(event);
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [angleOf, disabled],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      if (lastAngle.current === null) return;

      const angle = angleOf(event);
      let delta = angle - lastAngle.current;
      // Unwrap to the shortest way round, so crossing 180° does not swing the value.
      if (delta > 180) delta -= 360;
      else if (delta < -180) delta += 360;
      lastAngle.current = angle;

      onDelta((delta / DEGREES_PER_RANGE) * range);
    },
    [angleOf, onDelta, range],
  );

  const onPointerUp = useCallback((): void => {
    lastAngle.current = null;
  }, []);

  const clamped = Math.min(1, Math.max(0, fraction));

  return (
    <div
      className={styles.dial}
      style={{
        background: `conic-gradient(var(--color-accent) 0 ${(clamped * 100).toFixed(2)}%, var(--color-neutral-800) 0 100%)`,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label="Board size"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped * 100)}
      aria-valuetext={`${percent}%`}
      aria-disabled={disabled}
      onKeyDown={(event) => {
        if (disabled) return;
        const step = event.shiftKey ? range / 10 : range / 50;
        if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
          event.preventDefault();
          onDelta(step);
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') {
          event.preventDefault();
          onDelta(-step);
        }
      }}
    >
      <div className={styles.face}>
        <div>
          <div className={styles.percent}>
            {percent}
            <span className={styles.unit}>%</span>
          </div>
          <div className={styles.caption}>size</div>
        </div>
      </div>
      <div
        className={styles.knob}
        style={{ transform: `rotate(${clamped * 360}deg) translateY(var(--knob-radius))` }}
      />
    </div>
  );
}
